import { useState } from 'react';
import { Alert } from '../../components/Overlay.tsx';
import { NavBar } from '../../components/NavBar.tsx';
import { Icon } from '../../components/Icon.tsx';
import { GroupPhoto } from '../../components/GroupPhoto.tsx';
import { GROUP_PHOTOS } from '../../domain.ts';
import type { GroupPhoto as PhotoKey } from '../../domain.ts';

/**
 * F2-1 — 모임 사진 고르기 팝업.
 *
 * 사진 칸의 ＋ 를 누르면 «어디서 고를지»를 먼저 묻습니다. 사진첩은 권한이
 * 필요하고 기본 사진은 필요 없어서, 두 길의 무게가 다릅니다 — 한쪽을 먼저
 * 열어 놓고 다른 쪽으로 가게 하면 권한 창을 헛되이 띄우게 됩니다.
 *
 * 취소 · 어두운 배경 어느 쪽을 눌러도 팝업만 닫히고 F2 는 그대로입니다 —
 * C4-1 과 같은 규칙입니다.
 */

interface PickProps {
  onLibrary: () => void;
  onDefaults: () => void;
  onClose: () => void;
}

export function PhotoSourcePick({ onLibrary, onDefaults, onClose }: PickProps) {
  return (
    <Alert
      title="모임 사진"
      onClose={onClose}
      actions={
        <button type="button" className="btn btn--secondary" onClick={onClose}>
          취소
        </button>
      }
    >
      <button type="button" className="mrow" onClick={onLibrary}>
        <Icon name="image" size={20} />
        <span className="mrow__grow">
          사진첩에서 선택
          <span className="mrow__sub">기기의 사진첩을 엽니다</span>
        </span>
      </button>

      <button type="button" className="mrow" onClick={onDefaults}>
        <Icon name="tiles" size={20} />
        <span className="mrow__grow">
          기본 사진에서 선택
          <span className="mrow__sub">어플이 준비한 사진 열두 장</span>
        </span>
      </button>
    </Alert>
  );
}

/**
 * F2-3 — 기본 사진 고르기.
 *
 * 세 줄 격자로 열두 장을 보여 줍니다. 맨 위 미리 보기는 **목록(F1)에서 보이는
 * 크기**입니다 — 작은 칸에서도 알아보이는지 고르면서 확인합니다.
 *
 * 사진은 새벽 · 산 · 바다처럼 글자 없는 그림이라, 어떤 모임 이름과 나란히
 * 놓여도 겹쳐 읽히지 않습니다.
 *
 * ‹ 는 고르지 않고 돌아갑니다 — 한 단계만 되돌리는 공통 규칙 그대로입니다.
 */

interface DefaultsProps {
  /** 지금 들어 있는 사진. 격자에서 체크가 붙어 있습니다 */
  photo?: PhotoKey;
  name: string;
  onBack: () => void;
  onPick: (photo: PhotoKey) => void;
}

export function PhotoDefaults({ photo, name, onBack, onPick }: DefaultsProps) {
  const [chosen, setChosen] = useState<PhotoKey | undefined>(photo);

  return (
    <div className="screen">
      <NavBar
        title="기본 사진"
        start={{ icon: 'back', label: '돌아가기', onClick: onBack }}
        end={
          chosen === undefined
            ? undefined
            : { icon: 'check', label: '선택', accent: true, onClick: () => onPick(chosen) }
        }
      />

      <div className="screen__body">
        <p className="section-title">미리 보기</p>
        {/* 목록에서 보이는 크기 그대로 — 작은 칸에서 알아보이는지 확인합니다 */}
        <div className="ptpreview">
          {chosen === undefined ? (
            <span className="avatar avatar--square">{name.slice(0, 2) || '모임'}</span>
          ) : (
            <GroupPhoto photo={chosen} avatar />
          )}
          <span className="row__grow">
            <span className="row__name">{name || '모임 이름'}</span>
            <span className="row__sub">목록에서 이렇게 보입니다</span>
          </span>
        </div>

        <p className="section-title">
          기본 사진 <span>{GROUP_PHOTOS.length}</span>
        </p>
        <div className="ptgrid">
          {GROUP_PHOTOS.map((key) => {
            const on = key === chosen;
            return (
              <button
                key={key}
                type="button"
                className={on ? 'ptgrid__cell ptgrid__cell--on' : 'ptgrid__cell'}
                aria-pressed={on}
                aria-label={`사진 ${key}`}
                onClick={() => setChosen(key)}
              >
                {on && (
                  <span className="ptgrid__check">
                    <Icon name="check" size={12} />
                  </span>
                )}
                <GroupPhoto photo={key} />
              </button>
            );
          })}
        </div>

        <p className="hint">
          하나를 눌러 고르고 오른쪽 위 <b>선택</b>을 누르면 사진 칸에 들어갑니다.
        </p>
      </div>
    </div>
  );
}
